import { useEffect, useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import uniqBy from 'lodash.uniqby';
import InfiniteScroll from 'react-infinite-scroll-component';
import { MasonryPhotoAlbum } from 'react-photo-album';
import 'react-photo-album/masonry.css';
import Skeleton from '@mui/material/Skeleton';
import ImageListItem from '@mui/material/ImageListItem';
import IntegrationRequired from '@features/IntegrationRequired';
import { ModalConfirm } from '@components/Modal';
import Button from '@components/Button';
import SafeImage from '@components/SafeImage';
import { ImportIcon } from 'assets/images/icons';
import { profilesMediaActions, alertActions } from '@actions';
import { profilesMediaService } from '@services';
import { AUTHOR, FORMAT, MEDIA_STATUS } from '@constants/profiles-media';
import { AccIntegrationName } from '@constants/integrations';
import { isIntegrationConnected } from '@helpers/integrations';
import { openPopupWindow } from '@helpers/popup-window';
import { useTranslation, useLocations } from '@hooks';
import FiltersPanel from './FiltersPanel';
import ViewModal from './ViewModal';
import UploadModal from './UploadModal';
import MediaInfo from './MediaInfo';
import { getMediaAction } from './mediaActions';
import { Container, FiltersWrapper, Content, Badge } from './styles';

const DEFAULT_SKIP = 20;

const ProfilesMediaPage = () => {
    const [filter, setFilter] = useState({
        companies: [],
        formats: [FORMAT.PHOTO, FORMAT.VIDEO],
        categories: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
        authors: [AUTHOR.BUSINESS, AUTHOR.CUSTOMER],
        timeframe: {
            start: null,
            end: null,
        },
    });
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isViewModalOpen, setViewModalOpen] = useState(false);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isUploadModalOpen, setUploadModalOpen] = useState(false);
    const [loadedImages, setLoadedImages] = useState({});
    const [failedImages, setFailedImages] = useState({});

    const account = useSelector(state => state.account?.account);
    const companies = useSelector(state => state.account?.account?.companies || []);
    const language = useSelector(state => state.auth.user?.language);
    const { data, loading, hasMore } = useSelector(state => state.profilesMedia);

    const firstRender = useRef(true);
    const paginationStart = useRef(0);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { mapLocationsToFormValue } = useLocations();
    const GOOGLE_CONNECTED = isIntegrationConnected(account, AccIntegrationName.Google);

    const availableCompanies = useMemo(
        () => companies.filter(item => item.profiles.some(i => i.type === AccIntegrationName.Google)),
        [companies],
    );

    useEffect(() => {
        setFilter(prev => ({ ...prev, companies: mapLocationsToFormValue(availableCompanies) }));
    }, [availableCompanies, dispatch]);

    const onApplyFilters = () => {
        paginationStart.current = 0;
        dispatch(
            profilesMediaActions.get(
                {
                    locations: filter.companies.map(item => item.id),
                    formats: filter.formats,
                    categories: filter.categories,
                    authors: filter.authors,
                    from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                    to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
                },
                DEFAULT_SKIP,
                paginationStart.current,
            ),
        );
    };

    useEffect(() => {
        if (filter.companies.length && GOOGLE_CONNECTED) {
            if (firstRender.current) {
                onApplyFilters();
                firstRender.current = false;
            }
        }
    }, [filter, GOOGLE_CONNECTED, dispatch]);

    const loadMore = () => {
        if (!hasMore) return;
        const newPaginationStart =
            paginationStart.current === 0 ? DEFAULT_SKIP : paginationStart.current + DEFAULT_SKIP;
        paginationStart.current = newPaginationStart;
        dispatch(
            profilesMediaActions.loadMore(
                {
                    locations: filter.companies.map(item => item.id),
                    formats: filter.formats,
                    categories: filter.categories,
                    authors: filter.authors,
                    from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                    to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
                },
                DEFAULT_SKIP,
                newPaginationStart,
            ),
        );
    };

    const onDelete = async () => {
        try {
            await profilesMediaService.deleteMedia(selectedImage.id);
            dispatch(profilesMediaActions.set(data.filter(item => item.id !== selectedImage.id)));
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.deleteFail')));
        }
        setDeleteModalOpen(false);
        setViewModalOpen(false);
        setSelectedImage(null);
    };

    const applyMediaUpdate = updatedItem => {
        dispatch(profilesMediaActions.updateItem(updatedItem));
        setSelectedImage(prev => (prev?.id === updatedItem.id ? updatedItem : prev));
    };

    const onReport = async mediaItem => {
        const reportWindow = openPopupWindow(mediaItem?.takedownUrl);
        if (!reportWindow) {
            dispatch(alertActions.error(t('alertMessages.popupBlocked')));
            return;
        }
        try {
            applyMediaUpdate(await profilesMediaService.reportMedia(mediaItem.id));
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.updateFail')));
        }
    };

    const onUndoReport = async mediaItem => {
        try {
            applyMediaUpdate(await profilesMediaService.unreportMedia(mediaItem.id));
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.updateFail')));
        }
    };

    const mediaActionHandlers = {
        onDelete: mediaItem => {
            setSelectedImage(mediaItem);
            setDeleteModalOpen(true);
        },
        onReport,
        onUndoReport,
    };

    const STATUS = {
        [MEDIA_STATUS.NORMAL]: '',
        [MEDIA_STATUS.NEW]: t('common.new', { count: 1 }),
        [MEDIA_STATUS.REPORTED]: t('common.reported'),
    };

    const failedCount = Object.keys(failedImages).length;

    useEffect(() => {
        if (failedCount > 0 && hasMore && !loading) {
            const visibleCount = data.length - failedCount;
            if (visibleCount < DEFAULT_SKIP) {
                loadMore();
            }
        }
    }, [failedCount]);

    const photos = useMemo(
        () =>
            uniqBy(data, 'id')
                .map(item => ({
                    src: item.format === FORMAT.PHOTO ? item.originalUrl : item.thumbnailUrl,
                    alt: 'google-photo',
                    key: item.id,
                    original: item,
                    width: 600,
                    height: 700,
                }))
                .filter(item => item.src),
        [data],
    );

    return !GOOGLE_CONNECTED ? (
        <IntegrationRequired googleOnly />
    ) : (
        <Container>
            <FiltersWrapper>
                <FiltersPanel
                    setFiltersOpened={setFiltersOpened}
                    filtersNumber={filterNumber}
                    filterOpened={filterOpened}
                    filter={filter}
                    setFilter={setFilter}
                    setFilterNumber={setFilterNumber}
                    onApplyFilters={onApplyFilters}
                />
                <Button
                    variant="outlined"
                    style={{ flexShrink: 0 }}
                    startIcon={<ImportIcon />}
                    onClick={() => setUploadModalOpen(true)}
                >
                    {t('buttons.upload')}
                </Button>
            </FiltersWrapper>
            <Content id="pl-media-list">
                {!loading && !!data.length && (
                    <InfiniteScroll
                        dataLength={data.length}
                        next={loadMore}
                        hasMore={hasMore}
                        loader={<div />}
                        scrollableTarget="pl-media-list"
                    >
                        <MasonryPhotoAlbum
                            photos={photos}
                            spacing={10}
                            columns={() => {
                                if (window.innerWidth < 601) return 1;
                                if (window.innerWidth < 1024) return 2;
                                if (window.innerWidth < 1200) return 3;
                                return 4;
                            }}
                            render={{
                                photo: (_, { photo, width }) => {
                                    return (
                                        <ImageListItem
                                            key={photo.key}
                                            sx={{
                                                width,
                                                position: 'relative',
                                                cursor: 'zoom-in',
                                                display: failedImages[photo.key] ? 'none' : undefined,
                                                '& .MuiImageListItem-img': {
                                                    borderRadius: '8px',
                                                },
                                                '&:hover': {
                                                    '& .MuiImageListItemBar-root': {
                                                        opacity: 1,
                                                    },
                                                },
                                            }}
                                            onClick={() => {
                                                setSelectedImage(photo.original);
                                                setViewModalOpen(true);
                                            }}
                                        >
                                            {loadedImages[photo.key] &&
                                                photo.original.status !== MEDIA_STATUS.NORMAL && (
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            display: 'flex',
                                                            top: 16,
                                                            left: 16,
                                                            borderRadius: '18px',
                                                            backgroundColor: '#fff',
                                                        }}
                                                    >
                                                        <Badge variant="caption" type={photo.original.status}>
                                                            {STATUS[photo.original.status]}
                                                        </Badge>
                                                    </div>
                                                )}
                                            <SafeImage
                                                src={photo.src}
                                                alt="google-photo"
                                                placeholder={
                                                    <Skeleton
                                                        variant="rectangular"
                                                        width="100%"
                                                        height={300}
                                                        sx={{ borderRadius: '8px' }}
                                                    />
                                                }
                                                style={{ width: '100%', height: 'auto' }}
                                                onLoad={() => {
                                                    if (!loadedImages[photo.key]) {
                                                        setLoadedImages(prev => ({ ...prev, [photo.key]: true }));
                                                    }
                                                }}
                                                onError={() => {
                                                    setFailedImages(prev => ({ ...prev, [photo.key]: true }));
                                                }}
                                            />
                                            {loadedImages[photo.key] && (
                                                <MediaInfo
                                                    action={getMediaAction(photo.original, mediaActionHandlers)}
                                                    data={photo.original}
                                                    language={language}
                                                />
                                            )}
                                        </ImageListItem>
                                    );
                                },
                            }}
                        />
                    </InfiniteScroll>
                )}
            </Content>
            {isViewModalOpen && (
                <ViewModal
                    isOpen={isViewModalOpen}
                    handleClose={() => setViewModalOpen(false)}
                    selectedImage={selectedImage}
                    action={getMediaAction(selectedImage, mediaActionHandlers)}
                />
            )}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => setDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDelete}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
            {isUploadModalOpen && (
                <UploadModal isOpen={isUploadModalOpen} handleClose={() => setUploadModalOpen(false)} />
            )}
        </Container>
    );
};

export default ProfilesMediaPage;
